<template>
  <FriedCard
    v-tooltip="
      showTooltip
        ? {
            content: t('you_are_not_the_sender'),
            offset: 10,
            placement: TooltipPlacement.Bottom,
            triggers: ['click'],
          }
        : null
    "
    :class="['comment-container', { selected, hovered, 'in-comment-history': inCommentHistory }]"
    :data-external="`comment-card-id:${comment.id}-btn`"
    tabindex="0"
    role="button"
    @click="$emit('select')"
    @mouseover="$emit('hover-change', true)"
    @mouseleave="$emit('hover-change', false)"
  >
    <CommentTargetText
      v-if="hasContent"
      class="comment-target"
      :target-text="comment.editorSelection.content"
      :is-resolved="isResolved"
      :is-ghost="isGhost"
    />
    <MessageContainer
      v-if="!isDraft"
      :class="{ 'is-resolved': isResolved }"
      :created-at="comment.createdAt || 0"
      :is-read="getIsRead(comment.reads, currentUser)"
      :is-resolvable="isResolvable"
      :read-only="readOnly"
      :created-by-meta-data="getUserMetaData(comment.createdBy, recipients, sender)"
      :text="comment.richText || ''"
      :expanded="selected"
      :read-events="mappedLatestReadEvents[comment.id]"
      :mention-participants="mentionParticipants"
      @resolve="handleResolve"
      @line-count="handleMessageLineCountChanged(comment.id, $event)"
    />
    <div v-for="reply in visibleReplies" :key="reply.id">
      <hr />
      <MessageContainer
        :class="{ 'is-resolved': isResolved }"
        :data-external="`conversation-card-replyId:${reply.id}-reply-container`"
        :created-at="reply.createdAt || 0"
        :is-read="getIsRead(reply.reads, currentUser)"
        :is-reply="true"
        :current-user="currentUser"
        :read-only="readOnly"
        :text="reply.richText || ''"
        :created-by-meta-data="getUserMetaData(reply.createdBy, recipients, sender)"
        :expanded="selected"
        :read-events="mappedLatestReadEvents[reply.id]"
        :mention-participants="mentionParticipants"
        @line-count="handleMessageLineCountChanged(reply.id, $event)"
      />
    </div>
    <ConversationCardButtons
      v-if="showConversationCardButtons"
      :show-re-open-comment-button="isResolved && isCurrentUserCreator"
      :show-go-to-comment-button="showGoToCommentButton"
      :show-view-entire-thread-button="showViewEntireThreadButton"
      @re-open="handleReopen"
      @go-to="$emit('go-to-comment')"
    />
    <template v-if="showCommentInputControls">
      <CommentInput
        v-show="isDraft || selected"
        :mention-participants="filteredMentionParticipants"
        :data-external="`comment-card-id:${comment.id}-input:`"
        :current-user="currentUser"
        :value="richTextValue"
        :is-temporary="!!comment.isTemporary"
        :placeholder="evaluatePlaceholder"
        :selected="selected"
        :mentions-enabled="mentionsEnabled"
        @input="$event => isDraft && emitTextDebounce($event)"
        @submit="handleSubmit"
        @discard="handleDiscard"
      />
    </template>
    <CommentDraftStatus v-if="comment.status === CommentStatus.Draft" :comment-id="comment.id" />
  </FriedCard>
</template>
<script lang="ts">
import type { Comment } from '@getaccept/lib-shared-new/src/contextual-commenting/types/comment';
import {
  Placement as TooltipPlacement,
  AvatarSize,
  IconSize,
  ParagraphSize,
} from '@getaccept/fried-tofu';
import type { ComputedRef, PropType, Ref } from 'vue';
import { computed, defineComponent, ref, toRefs } from 'vue';
import debounce from 'debounce';
import { t } from '../../../helpers/translation.helper';
import type { Recipient } from '../../../types/Recipient';
import type { User } from '../../../users/types/user';
import CommentInput from '../../components/CommentInput.vue';
import CommentTargetText from '../../components/CommentTargetText.vue';
import MessageContainer from '../../components/MessageContainer.vue';
import { useCommentMetadata } from '../../composables/comment-metadata.composable';
import { useReadEvents } from '../../composables/read-events.composable';
import { CommentsHelper } from '../../helpers/comments.helper';
import { MentionsHelper } from '../../mentions/helpers/mentions.helper';
import type { MentionParticipant } from '../../mentions/types/mention-participant';
import { CommentStatus } from '../../types/comment-status';
import type { CurrentUser } from '../../types/current-user';
import type { UpdateCommentDto } from '../../types/update-comment-dto';
import CommentDraftStatus from './CommentDraftStatus.vue';
import ConversationCardButtons from './ConversationCardButtons.vue';

export default defineComponent({
  components: {
    CommentInput,
    MessageContainer,
    CommentTargetText,
    ConversationCardButtons,
    CommentDraftStatus,
  },
  props: {
    comment: {
      type: Object as PropType<Comment>,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    hovered: {
      type: Boolean,
      default: false,
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    inCommentHistory: {
      type: Boolean,
    },
    mentionsEnabled: {
      type: Boolean,
    },
  },
  emits: [
    'select',
    'hover-change',
    'go-to-comment',
    'update',
    'create-reply',
    'deselect',
    'delete',
  ],
  setup(props, { emit }) {
    const {
      comment,
      selected,
      hovered,
      currentUser,
      recipients,
      sender,
      readOnly,
      inCommentHistory,
    } = toRefs(props);

    const messageIdLineCountMap: Ref<{ [id: string]: number }> = ref({});

    const mentionParticipants: ComputedRef<MentionParticipant[]> = computed(() =>
      MentionsHelper.getMentionParticipants(recipients.value, sender.value)
    );

    const filteredMentionParticipants: ComputedRef<MentionParticipant[]> = computed(() =>
      MentionsHelper.filterIneligibleParticipants(currentUser.value, mentionParticipants.value)
    );

    const {
      isDraft,
      isResolved,
      isGhost,
      isResolvable,
      isCurrentUserCreator,
      richTextValue,
      visibleReplies,
    } = useCommentMetadata(comment, currentUser, readOnly, selected);

    const showTooltip = computed(() => {
      if (currentUser.value.recipientId || !selected.value) {
        return false;
      }

      return currentUser.value.userId !== (sender.value as User).id;
    });

    const repliesCanCollapse = computed(() => comment.value.replies.length > 2);

    const evaluatePlaceholder = computed(() =>
      props.mentionsEnabled
        ? t('cc_input_field_type_comment_here_mention', { trigger: '@' })
        : t('cc_input_field_type_comment_here')
    );

    const conversationCanCollapse = computed(() => {
      const hasLongContent = Object.values(messageIdLineCountMap.value).some(
        lineCount => lineCount > 2
      );

      return repliesCanCollapse.value || hasLongContent;
    });

    const hasContent = computed(() => CommentsHelper.hasContent(comment.value));

    const showGoToCommentButton = computed(
      () =>
        inCommentHistory.value &&
        (selected.value || hovered.value) &&
        CommentsHelper.isCommentVisible(comment.value)
    );

    const emitText = ({ richText, text }: { richText: string; text: string }) => {
      const payload: Partial<UpdateCommentDto> = { richText, text };
      emit('update', payload);
    };

    const handleResolve = () => {
      const payload: Partial<UpdateCommentDto> = { status: CommentStatus.Resolved };
      emit('update', payload);
    };

    const handleReopen = () => {
      const payload: Partial<UpdateCommentDto> = { status: CommentStatus.Active };
      emit('update', payload);
    };

    const handleSubmit = ({ richText, text }: { richText: string; text: string }) => {
      if (isDraft.value) {
        emitTextDebounce.clear();
        const payload: Partial<UpdateCommentDto> = { richText, text, status: CommentStatus.Active };
        emit('update', payload);
      } else {
        emit('create-reply', { commentId: comment.value.id, richText, text });
      }
    };

    const handleMessageLineCountChanged = (id: string, lineCount: number) => {
      messageIdLineCountMap.value = {
        ...messageIdLineCountMap.value,
        [id]: lineCount,
      };
    };

    const emitTextDebounce = debounce(emitText, 500);

    const showCommentInputControls = computed(
      () => !readOnly.value && !isResolved.value && !inCommentHistory.value
    );

    const showViewEntireThreadButton = computed(
      () => conversationCanCollapse.value && !selected.value
    );

    const { mappedLatestReadEvents } = useReadEvents(
      comment,
      recipients.value,
      sender.value,
      currentUser
    );

    const showConversationCardButtons = computed(
      () =>
        (isResolved.value && isCurrentUserCreator.value) ||
        showGoToCommentButton.value ||
        showViewEntireThreadButton.value
    );

    const handleDiscard = () => {
      if (isDraft.value) {
        emit('delete');
        return;
      }
      emit('deselect');
    };

    return {
      AvatarSize,
      TooltipPlacement,
      CommentStatus,
      IconSize,

      emitTextDebounce,
      getIsRead: CommentsHelper.isRead,
      getUserMetaData: CommentsHelper.getUserMetaData,
      t,
      handleMessageLineCountChanged,
      handleResolve,
      handleSubmit,
      handleReopen,

      isCurrentUserCreator,
      isDraft,
      isGhost,
      isResolved,
      isResolvable,

      richTextValue,
      visibleReplies,
      conversationCanCollapse,
      ParagraphSize,
      hasContent,
      showTooltip,
      showCommentInputControls,
      showViewEntireThreadButton,
      showGoToCommentButton,
      mappedLatestReadEvents,
      evaluatePlaceholder,

      mentionParticipants,
      filteredMentionParticipants,
      showConversationCardButtons,
      handleDiscard,
    };
  },
});
</script>
<style lang="scss" scoped>
.comment-container {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-75) var(--spacing-100) var(--spacing-100) var(--spacing-100);
  position: relative;
  outline: 1px solid var(--gray-92);
  outline-offset: -1px;
  box-shadow: none;

  &.selected,
  &.hovered {
    outline: 2px solid var(--warning-dark);
    outline-offset: -2px;
  }

  &.selected:not(.in-comment-history) {
    box-shadow: var(--shadow-1);
  }

  .comment-target {
    width: 100%;

    :deep(.floater-reference-container) {
      width: 100%;
    }
  }

  .is-resolved {
    opacity: 0.5;
  }

  hr {
    width: 100%;
    border: 0;
    border-top: 1px solid var(--gray-96);
    margin-bottom: var(--spacing-50);
  }
}
</style>

<style lang="scss">
.tooltip.popover.excerpt-tooltip {
  max-width: 300px;
}
</style>
